import React, { memo, useMemo } from 'react'
import tw from 'twin.macro'
import { useSelector } from 'react-redux'
import { TableTabs } from 'src/components/shared/table/table-tabs/TableTabs'
import type { TableTabItem } from 'src/components/shared/table/table-tabs/TableTabs'
import { documentReviewTabTitles } from 'src/constants/document-review/constants'
import { DocumentReviewTab } from 'src/constants/document-review/enums'
import { getDocumentReviewSteps, getDocumentReviewStepsLoading } from 'src/selectors/documentReview'
import { getMcReviewDocuments, getMcReviewDocumentsLoading } from 'src/selectors/mcDocumentReview'
import { addS } from 'src/utils/baseUtils'
import { GSIcon } from 'src/components/shared/GSIcon'

interface Props {
  selectedTab: DocumentReviewTab
  /** Only the queues the reviewer may view — see `resolveDocumentReviewQueueTab`. */
  availableTabs: DocumentReviewTab[]
  onChange(tab: DocumentReviewTab): void
}

// Central icons paint via `currentColor`, so colour is set through the text colour, not `fill`.
const selectedIconStyles = tw`text-primary-600`
const iconStyles = tw`text-shades-1000`

/** Contractor / Master contractor tabs, the same shape as the customer list's Active / Inactive. */
const DocumentReviewTabsComponent = ({ selectedTab, availableTabs, onChange }: Props) => {
  const contractorSteps = useSelector(getDocumentReviewSteps)
  const isContractorLoading = useSelector(getDocumentReviewStepsLoading)
  const mcDocuments = useSelector(getMcReviewDocuments)
  const isMcLoading = useSelector(getMcReviewDocumentsLoading)

  const tabs = useMemo(() => {
    // No count until the first load settles — a "0 documents" subtitle during the request reads
    // as an empty queue, which is the opposite of what a reviewer needs to know.
    const hasContractorCount = !isContractorLoading || contractorSteps.length > 0
    const hasMcCount = !isMcLoading || mcDocuments.length > 0

    const items: Array<TableTabItem<DocumentReviewTab>> = [
      {
        value: DocumentReviewTab.Contractor,
        title: documentReviewTabTitles[DocumentReviewTab.Contractor],
        subtitle: hasContractorCount
          ? `${contractorSteps.length} ${addS(contractorSteps.length, 'document')}`
          : '',
        dataTestId: `document-review-tab-${DocumentReviewTab.Contractor}`,
        renderIcon: (selected: boolean) => {
          return (
            <GSIcon
              iconName="gs_driver_front"
              size={20}
              css={selected ? selectedIconStyles : iconStyles}
            />
          )
        },
      },
      {
        value: DocumentReviewTab.MasterContractor,
        title: documentReviewTabTitles[DocumentReviewTab.MasterContractor],
        subtitle: hasMcCount ? `${mcDocuments.length} ${addS(mcDocuments.length, 'document')}` : '',
        dataTestId: `document-review-tab-${DocumentReviewTab.MasterContractor}`,
        renderIcon: (selected: boolean) => {
          return (
            <GSIcon
              iconName="gs_buildings"
              size={20}
              css={selected ? selectedIconStyles : iconStyles}
            />
          )
        },
      },
    ]

    return items.filter((item) => {
      return availableTabs.includes(item.value)
    })
  }, [availableTabs, contractorSteps, isContractorLoading, mcDocuments, isMcLoading])

  return <TableTabs<DocumentReviewTab> tabs={tabs} selectedValue={selectedTab} onClick={onChange} />
}

export const DocumentReviewTabs = memo(DocumentReviewTabsComponent)
